"use client";
import { useEffect, useRef } from "react";

import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

// An image resampled into a grid of perlin-driven dots, with a staggered
// materialize/dissolve on show/hide. All constants below are tunable.
const SPACING = 7; // px between dot samples
const SIZE_PCT = 0.9; // dot radius as a fraction of half the spacing
const SCALE = 0.004; // noise frequency
const SPEED = 0.5; // animation speed
const AMPLITUDE = 1; // size drive strength [0..1]
const EDGE_FALLOFF = 0.005; // static-border thickness, as a fraction of each edge
const SAMPLE_MAX_DIM = 900; // image is downscaled to this before sampling
const BACKGROUND = "#000000"; // backdrop the dots sit on
const FALLBACK_RGB: readonly [number, number, number] = [236, 232, 225]; // dot color when the image can't be sampled (CORS)
const ENTRANCE_MS = 400; // fade-in duration, ms
const EXIT_MS = 400; // fade-out duration, ms
const STAGGER = 0.6; // per-dot reveal spread [0..1); 0 = all dots together

const smoothstep = (x: number) => x * x * (3 - 2 * x);
const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/** A freshly-seeded 3D perlin sampler `(x, y, z) → ~[-1, 1]`. */
function createNoise3(): (x: number, y: number, z: number) => number {
  const perm = new Uint8Array(512);
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = p[i]!;
    p[i] = p[j]!;
    p[j] = tmp;
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255]!;

  const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
  const lerp = (a: number, b: number, t: number) => a + t * (b - a);
  const grad = (h: number, x: number, y: number, z: number) => {
    const g = h & 15;
    const u = g < 8 ? x : y;
    const v = g < 4 ? y : g === 12 || g === 14 ? x : z;
    return (g & 1 ? -u : u) + (g & 2 ? -v : v);
  };

  return (x, y, z) => {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    const Z = Math.floor(z) & 255;
    x -= Math.floor(x);
    y -= Math.floor(y);
    z -= Math.floor(z);
    const u = fade(x);
    const v = fade(y);
    const w = fade(z);
    const A = perm[X]! + Y;
    const AA = perm[A]! + Z;
    const AB = perm[A + 1]! + Z;
    const B = perm[X + 1]! + Y;
    const BA = perm[B]! + Z;
    const BB = perm[B + 1]! + Z;
    return lerp(
      lerp(
        lerp(grad(perm[AA]!, x, y, z), grad(perm[BA]!, x - 1, y, z), u),
        lerp(grad(perm[AB]!, x, y - 1, z), grad(perm[BB]!, x - 1, y - 1, z), u),
        v,
      ),
      lerp(
        lerp(grad(perm[AA + 1]!, x, y, z - 1), grad(perm[BA + 1]!, x - 1, y, z - 1), u),
        lerp(grad(perm[AB + 1]!, x, y - 1, z - 1), grad(perm[BB + 1]!, x - 1, y - 1, z - 1), u),
        v,
      ),
      w,
    );
  };
}

interface DotGrid {
  px: Float32Array;
  py: Float32Array;
  cr: Uint8Array;
  cg: Uint8Array;
  cb: Uint8Array;
  phase: Float32Array;
  n: number;
  sampleW: number;
  sampleH: number;
}

export interface DotFieldProps {
  /** Image to resample into the dot field. */
  imageUrl: string;
  /** Extra classes for the wrapper. */
  className?: string;
  /** Whether the field is shown; flipping to false fades it out, then calls onExited. Default true. */
  show?: boolean;
  /** Fade in on mount rather than cutting in. Default true. */
  transitionIn?: boolean;
  /** Fade out when hidden rather than cutting out. Default true. */
  transitionOut?: boolean;
  /** Called once the exit fade finishes, so the parent can unmount. */
  onExited?: () => void;
}

/**
 * Resamples `imageUrl` into a perlin-driven dot field with a staggered
 * materialize/dissolve. Presentational — the caller owns mounting and `show`.
 */
export function DotField({
  imageUrl,
  className,
  show = true,
  transitionIn = true,
  transitionOut = true,
  onExited,
}: DotFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Mirror live props into refs so the render loop reads current values without restarting.
  const showRef = useRef(show);
  showRef.current = show;
  const transInRef = useRef(transitionIn);
  transInRef.current = transitionIn;
  const transOutRef = useRef(transitionOut);
  transOutRef.current = transitionOut;
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const noise3 = createNoise3();
    let dots: DotGrid | null = null;
    let cancelled = false;

    // Resample the image into a dot grid; if the canvas is tainted (CORS), fall back to a flat color.
    const buildDots = (source: CanvasImageSource, srcW: number, srcH: number) => {
      const scale = Math.min(1, SAMPLE_MAX_DIM / Math.max(srcW, srcH));
      const w = Math.max(1, Math.round(srcW * scale));
      const h = Math.max(1, Math.round(srcH * scale));
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const octx = off.getContext("2d", { willReadFrequently: true });
      if (!octx) return;
      octx.drawImage(source, 0, 0, w, h);

      let data: Uint8ClampedArray | null = null;
      try {
        data = octx.getImageData(0, 0, w, h).data;
      } catch {
        data = null;
      }

      const cols = Math.floor(w / SPACING);
      const rows = Math.floor(h / SPACING);
      const n = cols * rows;
      const px = new Float32Array(n);
      const py = new Float32Array(n);
      const cr = new Uint8Array(n);
      const cg = new Uint8Array(n);
      const cb = new Uint8Array(n);
      const phase = new Float32Array(n);
      let i = 0;
      for (let gy = 0; gy < rows; gy++) {
        for (let gx = 0; gx < cols; gx++) {
          const sx = Math.floor(gx * SPACING + SPACING / 2);
          const sy = Math.floor(gy * SPACING + SPACING / 2);
          px[i] = sx;
          py[i] = sy;
          phase[i] = Math.random();
          if (data) {
            const idx = (sy * w + sx) * 4;
            cr[i] = data[idx]!;
            cg[i] = data[idx + 1]!;
            cb[i] = data[idx + 2]!;
          } else {
            cr[i] = FALLBACK_RGB[0];
            cg[i] = FALLBACK_RGB[1];
            cb[i] = FALLBACK_RGB[2];
          }
          i++;
        }
      }
      dots = { px, py, cr, cg, cb, phase, n, sampleW: w, sampleH: h };
    };

    // Decode the image for sampling without tainting the canvas
    const loadViaImg = (useCors: boolean) => {
      const img = new window.Image();
      if (useCors) img.crossOrigin = "anonymous";
      img.onload = () => {
        if (!cancelled) buildDots(img, img.naturalWidth, img.naturalHeight);
      };
      img.onerror = () => {
        if (!cancelled && useCors) loadViaImg(false);
      };
      img.src = imageUrl;
    };
    void fetch(imageUrl, { mode: "cors", cache: "reload" })
      .then((res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.blob();
      })
      .then((blob) => createImageBitmap(blob))
      .then((bitmap) => {
        if (cancelled) {
          bitmap.close();
          return;
        }
        buildDots(bitmap, bitmap.width, bitmap.height);
        bitmap.close();
      })
      .catch(() => {
        if (!cancelled) loadViaImg(true);
      });

    let raf = 0;
    let t = 0;
    let last = performance.now();
    let progress = transInRef.current ? 0 : 1; // 0 = hidden, 1 = fully materialized
    let exited = false;

    const render = (now: number) => {
      raf = requestAnimationFrame(render);
      const dt = (now - last) / 1000;
      t += dt * SPEED;
      last = now;

      // Ease progress toward its target (1 shown, 0 hidden); when a hide reaches 0, tell the parent.
      const entering = showRef.current;
      const target = entering ? 1 : 0;
      const animate = entering ? transInRef.current : transOutRef.current;
      const step = animate ? (dt * 1000) / (entering ? ENTRANCE_MS : EXIT_MS) : 1;
      progress = progress < target ? Math.min(target, progress + step) : Math.max(target, progress - step);
      if (entering) exited = false;
      else if (progress <= 0 && !exited) {
        exited = true;
        onExitedRef.current?.();
      }
      const ease = smoothstep(progress);

      if (overlayRef.current) overlayRef.current.style.opacity = String(ease);

      const dpr = window.devicePixelRatio || 1;
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      if (cw === 0 || ch === 0) return;
      if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
        canvas.width = Math.round(cw * dpr);
        canvas.height = Math.round(ch * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cw, ch);
      ctx.globalAlpha = ease; // backdrop fades with progress so the image shows through
      ctx.fillStyle = BACKGROUND;
      ctx.fillRect(0, 0, cw, ch);
      if (!dots) return;

      const { px, py, cr, cg, cb, phase, n, sampleW, sampleH } = dots;
      // Contain-fit the sampled image inside the canvas (matches <img object-contain>).
      const fit = Math.min(cw / sampleW, ch / sampleH);
      const offX = (cw - sampleW * fit) / 2;
      const offY = (ch - sampleH * fit) / 2;
      const baseR = (SPACING / 2) * SIZE_PCT * fit;

      for (let i = 0; i < n; i++) {
        const sx = px[i]!;
        const sy = py[i]!;

        // Per-dot staggered fade+grow, so dots materialize/dissolve at scattered times.
        const grow = smoothstep(clamp01((ease - phase[i]! * STAGGER) / (1 - STAGGER)));
        if (grow <= 0) continue;

        // Edge falloff: outer dots stay static (animation ramps in over EDGE_FALLOFF).
        const edgeDist = Math.min(Math.min(sx, sampleW - sx) / sampleW, Math.min(sy, sampleH - sy) / sampleH);
        const amp = AMPLITUDE * smoothstep(clamp01(edgeDist / EDGE_FALLOFF));

        const norm = (noise3(sx * SCALE, sy * SCALE, t) + 1) * 0.5;
        const r = baseR * Math.max(0, 1 - amp * (1 - norm)) * grow;
        if (r < 0.2) continue;

        // Dots scale in place — no positional jitter, so the field stays on its grid.
        const dx = offX + sx * fit;
        const dy = offY + sy * fit;

        ctx.globalAlpha = grow;
        ctx.fillStyle = `rgb(${cr[i]!}, ${cg[i]!}, ${cb[i]!})`;
        ctx.beginPath();
        ctx.arc(dx, dy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };
    raf = requestAnimationFrame(render);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [imageUrl]);

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 z-20 flex h-full w-full items-center justify-center",
        className,
      )}
    >
      <canvas className="absolute inset-0 h-full w-full" ref={canvasRef} />
      {/* Logo panel — opacity is driven per-frame by the render loop. */}
      <div
        className="relative flex items-center justify-center p-16"
        ref={overlayRef}
        style={{ opacity: transitionIn ? 0 : 1 }}
      >
        <div className="absolute inset-0 h-full w-full rounded-md bg-black/50 blur-2xl" />
        <Icon className="z-10 h-10 w-auto animate-pulse text-white" name="logo" />
      </div>
    </div>
  );
}
